import {
  useQuery,
  useMutation,
  resolved,
  query as gqtyQuery,
  ImageSupportedVariant,
  ImageFileVersionFormat,
} from '@hapana/shared/web/services/gqty'
import {
  SITE_DATE_FORMAT_KEY,
  SITE_TIME_FORMAT_KEY,
} from '@hapana/shared/web/utils/date-format-utils'
import { useTranslation } from 'react-i18next'
import { useQuery as useReactQuery, useQueryClient } from 'react-query'
import { getSiteById, getSiteConnectedSharedSiteIds } from '../../services/sites.service'
import { getConfiguration } from '@hapana/shared/web/services/configuration.service'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import {
  useAuditTracker,
  useAuditTrackerConfigOptional,
  AuditAction,
  AuditResource,
  AuditSeverity,
} from '@hapana/shared/web/hooks'
import type {
  MutationOptions,
  UpdateSiteSettingsBusinessCustomizationInputInput,
  UpdateSiteSettingsRegionalCommunicationInputInput,
  UpdateSiteInputInput,
} from '@hapana/shared/web/services/gqty'

export const ALL_SITES_PAGE_SIZE = 500

interface UseSitesOptions {
  search?: string
  pageSize?: number
}

export const useSites = (options: UseSitesOptions = {}) => {
  const { search, pageSize = 20 } = options
  const query = useQuery({ suspense: false })

  const sitesResult = query.sites({
    search: search || undefined,
    pageSize,
  })

  const sites = (sitesResult?.results || [])
    .map((result) => {
      const site = result.row
      if (site) {
        const fields = [site.siteId, site.name, site.status, site.timezone]
        void fields
      }
      return site
    })
    .filter((site) => site.siteId != null)

  return {
    data: sites,
    isLoading: query.$state.isLoading,
    error: query.$state.error,
    refetch: () => query.$refetch(),
  }
}

export const useConnectedSites = (siteId: string | undefined) => {
  const { data: connectedIds = [] } = useReactQuery<string[]>(
    [getSiteConnectedSharedSiteIds.key, { siteId }],
    getSiteConnectedSharedSiteIds.query,
    {
      select: (data: { site?: { connectedSharedSiteIds?: string[] } } | undefined) =>
        data?.site?.connectedSharedSiteIds ?? [],
      enabled: !!siteId,
    }
  )

  const { data: allSites = [], isLoading } = useSites({ pageSize: ALL_SITES_PAGE_SIZE })

  const connectedSites = allSites.filter((s) => s?.siteId && connectedIds.includes(s.siteId))

  return { data: connectedSites, isLoading }
}

/**
 * Fetches site data via resolved() + React Query instead of GQty proxy useQuery.
 * Eliminates the proxy timing bug where nested settings fields temporarily
 * resolve to undefined during background refetches, causing values to disappear.
 * keepPreviousData ensures the UI always has data while refetching.
 */
export const SITE_BY_ID_KEY = 'gqty-site-by-id'

const fetchSiteById = async (siteId: string) => {
  const result = await resolved(
    () => {
      const site = gqtyQuery.site({ siteId })
      if (!site) {
        return null
      }

      const logoImageData = site.logoImage({
        input: { type: ImageSupportedVariant.thumbnail, format: ImageFileVersionFormat.webp },
      })

      // Access top-level fields before return to ensure GQty detects them
      const siteTimezone = site.timezone
      // ISO alpha-2 resolved via the federated country relation (e.g. "NZ"),
      // the reliable source for the phone-input default country. The free-text
      // addressCountry column is stored inconsistently ("NZ" vs "New Zealand").
      const siteCountryIsoCode = site.country?.isoCode

      return {
        siteId: site.siteId,
        name: site.name,
        description: site.description,
        countryId: site.countryId,
        countryIsoCode: siteCountryIsoCode,
        stateId: site.stateId,
        addressStreet: site.addressStreet,
        addressStreet2: site.addressStreet2,
        addressCity: site.addressCity,
        addressCountry: site.addressCountry,
        addressPostalCode: site.addressPostalCode,
        addressState: site.addressState,
        phone: site.phone,
        businessEmail: site.businessEmail,
        displayBusinessHours: site.displayBusinessHours,
        adminConfigurations: {
          monthlySmsCredits: site.adminConfigurations?.monthlySmsCredits,
        },
        businessHours: {
          hasCustomHours: site.businessHours?.hasCustomHours,
          startTime: site.businessHours?.startTime,
          endTime: site.businessHours?.endTime,
          customHours: {
            monday: {
              startTime: site.businessHours?.customHours?.monday?.startTime,
              endTime: site.businessHours?.customHours?.monday?.endTime,
            },
            tuesday: {
              startTime: site.businessHours?.customHours?.tuesday?.startTime,
              endTime: site.businessHours?.customHours?.tuesday?.endTime,
            },
            wednesday: {
              startTime: site.businessHours?.customHours?.wednesday?.startTime,
              endTime: site.businessHours?.customHours?.wednesday?.endTime,
            },
            thursday: {
              startTime: site.businessHours?.customHours?.thursday?.startTime,
              endTime: site.businessHours?.customHours?.thursday?.endTime,
            },
            friday: {
              startTime: site.businessHours?.customHours?.friday?.startTime,
              endTime: site.businessHours?.customHours?.friday?.endTime,
            },
            saturday: {
              startTime: site.businessHours?.customHours?.saturday?.startTime,
              endTime: site.businessHours?.customHours?.saturday?.endTime,
            },
            sunday: {
              startTime: site.businessHours?.customHours?.sunday?.startTime,
              endTime: site.businessHours?.customHours?.sunday?.endTime,
            },
          },
        },
        timezone: siteTimezone,
        settings: {
          location: {
            businessInformation: site.settings?.location?.businessInformation,
            socialMedia: site.settings?.location?.socialMedia,
            configurations: {
              currency: site.settings?.location?.configurations?.currency,
              dateFormat: site.settings?.location?.configurations?.dateFormat,
              numberFormat: site.settings?.location?.configurations?.numberFormat,
              timeFormat: site.settings?.location?.configurations?.timeFormat,
              dashboardRevenuePeriod:
                site.settings?.location?.configurations?.dashboardRevenuePeriod,
            },
            checkIn: site.settings?.location?.checkIn,
            communications: site.settings?.location?.communications,
            tools: site.settings?.location?.tools,
            operations: {
              autoLogout: site.settings?.location?.operations?.autoLogout,
              autoLogoutMinutes: site.settings?.location?.operations?.autoLogoutMinutes,
              enableClientsToSignUp: site.settings?.location?.operations?.enableClientsToSignUp,
              cancellationReasons: site.settings?.location?.operations?.cancellationReasons,
              suspensionReasons: site.settings?.location?.operations?.suspensionReasons,
              businessSignUpFields: site.settings?.location?.operations?.businessSignUpFields,
              clientSignUpFields: site.settings?.location?.operations?.clientSignUpFields,
              systemPreferences: {
                tabsDisplayed:
                  site.settings?.location?.operations?.systemPreferences?.tabsDisplayed,
                defaultBusinessProfileTab:
                  site.settings?.location?.operations?.systemPreferences?.defaultBusinessProfileTab,
                availableClientsTabs:
                  site.settings?.location?.operations?.systemPreferences?.availableClientsTabs,
                defaultClientTab:
                  site.settings?.location?.operations?.systemPreferences?.defaultClientTab,
                showBusinessInfo:
                  site.settings?.location?.operations?.systemPreferences?.showBusinessInfo,
              },
            },
          },
          scheduler: site.settings?.scheduler,
          account: {
            billing: {
              enabled: site.settings?.account?.billing?.enabled,
              transactionFeePreference: site.settings?.account?.billing?.transactionFeePreference,
              retryFailedPayments: {
                intervalInDays:
                  site.settings?.account?.billing?.retryFailedPayments?.intervalInDays,
                maxAttempts: site.settings?.account?.billing?.retryFailedPayments?.maxAttempts,
                maxFailoversPerDay:
                  site.settings?.account?.billing?.retryFailedPayments?.maxFailoversPerDay,
                bankFailoverToCard: {
                  enabled:
                    site.settings?.account?.billing?.retryFailedPayments?.bankFailoverToCard
                      ?.enabled,
                },
              },
            },
            invoice: {
              email: site.settings?.account?.invoice?.email,
              label: site.settings?.account?.invoice?.label,
            },
            pointOfSale: {
              enableCashDrawer: site.settings?.account?.pointOfSale?.enableCashDrawer,
              itemTypes: site.settings?.account?.pointOfSale?.itemTypes,
            },
            tax: {
              enabled: site.settings?.account?.tax?.enabled,
            },
            payroll: {
              additionalTypes: site.settings?.account?.payroll?.additionalTypes,
            },
          },
          // OC-129: keep the full retail passthrough (fees/sales are read by schedule +
          // cancel-booking via this shared hook) and additionally resolve the nested
          // additionalBillingTypes.types list explicitly — gqty's resolved() snapshot drops
          // nested list fields unless iterated, so a bare spread leaves types empty.
          retail: {
            ...site.settings?.retail,
            additionalBillingTypes: {
              enabled: site.settings?.retail?.additionalBillingTypes?.enabled,
              types: site.settings?.retail?.additionalBillingTypes?.types?.map(
                ({ id, name, enabled }) => ({ id, name, enabled })
              ),
            },
          },
          customization: site.settings?.customization,
          updatedAt: site.settings?.updatedAt,
        },
        integrations: {
          classPass: {
            enabled: site.integrations?.classPass?.enabled ?? false,
          },
          frontDeskKiosk: {
            kioskUrlV1: site.integrations?.frontDeskKiosk?.kioskUrlV1 ?? null,
            betaVersion: {
              enabled: site.integrations?.frontDeskKiosk?.betaVersion?.enabled ?? false,
              kioskUrlV2: site.integrations?.frontDeskKiosk?.betaVersion?.kioskUrlV2 ?? null,
              introOffersEnabled:
                site.integrations?.frontDeskKiosk?.betaVersion?.introOffersEnabled ?? false,
              packagesMembershipsEnabled:
                site.integrations?.frontDeskKiosk?.betaVersion?.packagesMembershipsEnabled ?? false,
            },
          },
        },
        logoImage: logoImageData?.url ? { url: logoImageData.url } : null,
      }
    },
    { refetch: true }
  )
  return result
}

export const useGetSiteById = (siteId?: string, _options?: { refetchOnRender?: boolean }) => {
  const { data, isLoading, error, refetch } = useReactQuery(
    [SITE_BY_ID_KEY, siteId],
    () => fetchSiteById(siteId),
    {
      enabled: !!siteId,
      keepPreviousData: true,
      staleTime: 30_000,
      onSuccess: (siteData) => {
        if (!siteData) {
          return
        }
        const dateFormat = siteData.settings?.location?.configurations?.dateFormat
        if (dateFormat) {
          localStorage.setItem(SITE_DATE_FORMAT_KEY, dateFormat)
        }
        const timeFormat = siteData.settings?.location?.configurations?.timeFormat
        if (timeFormat) {
          localStorage.setItem(SITE_TIME_FORMAT_KEY, timeFormat)
        }
      },
    }
  )

  return {
    data: data ?? null,
    logoImage: data?.logoImage ?? null,
    isLoading,
    error,
    refetch,
  }
}

export const useUpdateSiteRegionalCommunication = (options?: MutationOptions) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()

  // Get audit config from context
  const auditConfig = useAuditTrackerConfigOptional()
  const { createMutationCallbacks } = useAuditTracker({
    ...auditConfig,
    corporateId: auditConfig?.corporateId ?? '',
  })

  let capturedSiteId: string
  let capturedInput: UpdateSiteSettingsRegionalCommunicationInputInput
  let cachedOldRegional: Record<string, unknown> | undefined

  return useMutation(
    (
      mutation,
      args: {
        siteId: string
        input: UpdateSiteSettingsRegionalCommunicationInputInput
      }
    ) => {
      const { siteId, input } = args
      capturedSiteId = siteId
      capturedInput = input

      // Capture old values from React Query cache before mutation
      const cachedSite = queryClient.getQueryData<Record<string, unknown>>([
        SITE_BY_ID_KEY,
        siteId,
      ]) as Record<string, unknown> | undefined
      const settings = cachedSite?.settings as Record<string, unknown> | undefined
      const location = settings?.location as Record<string, unknown> | undefined
      const configurations = location?.configurations as Record<string, unknown> | undefined
      cachedOldRegional = configurations ? { ...configurations } : undefined

      const result = mutation.updateSiteSettingsRegionalCommunication({
        siteId,
        input,
      })

      if (result) {
        void result.siteId
        void result.settings?.location?.configurations?.dateFormat
        void result.settings?.location?.configurations?.numberFormat
        void result.settings?.location?.configurations?.timeFormat
      }
      return result
    },
    {
      onComplete: (data) => {
        void queryClient.invalidateQueries([getSiteById.key])
        void queryClient.invalidateQueries([SITE_BY_ID_KEY])
        void queryClient.invalidateQueries([getConfiguration.key])

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.regionalCommunication.toasts.regionalCommunicationUpdated',
            'Regional communication updated successfully'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })

        // Track audit event
        const { onSuccess: auditOnSuccess } = createMutationCallbacks({
          action: AuditAction.SETTING_UPDATED,
          resource: AuditResource.SETTING,
          resourceId: capturedSiteId,
          changeType: 'update',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings',
          settingName: 'location.configurations.regionalCommunication',
          settingDisplayName: 'Regional Communication',
          getOldValue: () => cachedOldRegional,
          getNewValue: () => capturedInput,
          getSummary: () => `Regional communication settings updated`,
        })
        auditOnSuccess(data)

        const updatedDateFormat = data?.settings?.location?.configurations?.dateFormat
        if (updatedDateFormat) {
          localStorage.setItem(SITE_DATE_FORMAT_KEY, updatedDateFormat)
        }
        const updatedTimeFormat = data?.settings?.location?.configurations?.timeFormat
        if (updatedTimeFormat) {
          localStorage.setItem(SITE_TIME_FORMAT_KEY, updatedTimeFormat)
        }

        options?.onSuccess?.(data)
        closeDialog()
      },
      onError: (error: Error) => {
        // Track audit error
        const { onError: auditOnError } = createMutationCallbacks({
          action: AuditAction.SETTING_UPDATED,
          resource: AuditResource.SETTING,
          changeType: 'update',
          featureCategory: 'settings',
          settingName: 'location.configurations.regionalCommunication',
          settingDisplayName: 'Regional Communication',
        })
        auditOnError(error)

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.regionalCommunication.toasts.updateFailed',
            'Update failed'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
        options?.onError?.(error)
      },
    }
  )
}

export function useUpdateSiteTimezone() {
  const queryClient = useQueryClient()
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()

  const [mutate, { isLoading }] = useMutation(
    (mutation, { siteId, timezone }: { siteId: string; timezone: string }) => {
      const result = mutation.updateSiteTimezone({ siteId, timezone })
      return {
        siteId: result.siteId,
        timezone: result.timezone,
      }
    },
    {
      onComplete: () => {
        void queryClient.invalidateQueries([getSiteById.key])
        void queryClient.invalidateQueries([SITE_BY_ID_KEY])

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.configuration.toasts.timezoneUpdated',
            'Timezone updated successfully'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })

        closeDialog()
      },
      onError: () => {
        openToast({
          title: t(
            'settings.systemSettings.locationSettings.configuration.toasts.updateFailed',
            'Failed to update timezone'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
      },
    }
  )
  const updateSiteTimezone = ({ siteId, timezone }: { siteId: string; timezone: string }) =>
    mutate({ args: { siteId, timezone } })

  return { updateSiteTimezone, isLoading }
}

export const useUpdateSiteName = (options?: MutationOptions) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()

  // Get audit config from context
  const auditConfig = useAuditTrackerConfigOptional()
  const { createMutationCallbacks } = useAuditTracker({
    ...auditConfig,
    corporateId: auditConfig?.corporateId ?? '',
  })

  let capturedArgs: { siteId: string; name: string }
  let cachedOldName: string | undefined

  return useMutation(
    (
      mutation,
      args: {
        siteId: string
        name: string
      }
    ) => {
      const { siteId, name } = args
      capturedArgs = args

      // Capture old name from React Query cache before mutation
      const cachedSite = queryClient.getQueryData<Record<string, unknown>>([
        SITE_BY_ID_KEY,
        siteId,
      ]) as Record<string, unknown> | undefined
      cachedOldName = cachedSite?.name as string | undefined

      const result = mutation.updateSiteName({
        siteId,
        name,
      })
      if (result) {
        void result.siteId
        void result.name
      }
      return result
    },
    {
      onComplete: (data) => {
        void queryClient.invalidateQueries([getSiteById.key])
        void queryClient.invalidateQueries([SITE_BY_ID_KEY])

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.businessCustomization.toasts.businessNameUpdated',
            'Business name updated successfully'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })

        // Track audit event
        const { onSuccess: auditOnSuccess } = createMutationCallbacks({
          action: AuditAction.SETTING_UPDATED,
          resource: AuditResource.SETTING,
          resourceId: capturedArgs?.siteId,
          changeType: 'update',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings',
          settingName: 'site.name',
          settingDisplayName: 'Site Name',
          getOldValue: () => cachedOldName,
          getNewValue: () => capturedArgs?.name,
          getSummary: () => `Site name updated to "${capturedArgs?.name}"`,
        })
        auditOnSuccess(data)

        options?.onSuccess?.(data)
        closeDialog()
      },
      onError: (error: Error) => {
        // Track audit error
        const { onError: auditOnError } = createMutationCallbacks({
          action: AuditAction.SETTING_UPDATED,
          resource: AuditResource.SETTING,
          changeType: 'update',
          featureCategory: 'settings',
          settingName: 'site.name',
          settingDisplayName: 'Site Name',
        })
        auditOnError(error)

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.businessCustomization.toasts.updateFailed',
            'Update failed'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
        options?.onError?.(error)
      },
    }
  )
}

export const useUpdateSiteBusinessCustomization = (options?: MutationOptions) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()

  // Get audit config from context
  const auditConfig = useAuditTrackerConfigOptional()
  const { createMutationCallbacks } = useAuditTracker({
    ...auditConfig,
    corporateId: auditConfig?.corporateId ?? '',
  })

  let capturedSiteId: string
  let capturedInput: UpdateSiteSettingsBusinessCustomizationInputInput
  let cachedOldCustomization: Record<string, unknown> | undefined

  return useMutation(
    (
      mutation,
      args: {
        siteId: string
        input: UpdateSiteSettingsBusinessCustomizationInputInput
      }
    ) => {
      const { siteId, input } = args
      capturedSiteId = siteId
      capturedInput = input

      // Capture old values from React Query cache before mutation
      const cachedSite = queryClient.getQueryData<Record<string, unknown>>([
        SITE_BY_ID_KEY,
        siteId,
      ]) as Record<string, unknown> | undefined
      const settings = cachedSite?.settings as Record<string, unknown> | undefined
      cachedOldCustomization = settings?.customization as Record<string, unknown> | undefined

      const result = mutation.updateSiteSettingsBusinessCustomization({
        siteId,
        input,
      })
      if (result) {
        void result.siteId
        void result.name
        void result.description
        void result.settings?.location?.businessInformation
        void result.settings?.customization
      }
      return result
    },
    {
      onComplete: (data) => {
        void queryClient.invalidateQueries([getSiteById.key])
        void queryClient.invalidateQueries([SITE_BY_ID_KEY])

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.businessCustomization.toasts.businessCustomizationUpdated',
            'Business customization updated successfully'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })

        // Track audit event
        const { onSuccess: auditOnSuccess } = createMutationCallbacks({
          action: AuditAction.SETTING_UPDATED,
          resource: AuditResource.SETTING,
          resourceId: capturedSiteId,
          changeType: 'update',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings',
          settingName: 'settings.customization',
          settingDisplayName: 'Business Customization',
          getOldValue: () => cachedOldCustomization,
          getNewValue: () => capturedInput,
          getSummary: () => `Business customization settings updated`,
        })
        auditOnSuccess(data)

        options?.onSuccess?.(data)
        closeDialog()
      },
      onError: (error: Error) => {
        // Track audit error
        const { onError: auditOnError } = createMutationCallbacks({
          action: AuditAction.SETTING_UPDATED,
          resource: AuditResource.SETTING,
          changeType: 'update',
          featureCategory: 'settings',
          settingName: 'settings.customization',
          settingDisplayName: 'Business Customization',
        })
        auditOnError(error)

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.businessCustomization.toasts.updateFailed',
            'Update failed'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
        options?.onError?.(error)
      },
    }
  )
}

export const useUpdateSite = (options?: MutationOptions & { skipAudit?: boolean }) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const queryClient = useQueryClient()

  // Get audit config from context
  const auditConfig = useAuditTrackerConfigOptional()
  const { createMutationCallbacks } = useAuditTracker({
    ...auditConfig,
    corporateId: auditConfig?.corporateId ?? '',
  })

  let capturedSiteId: string
  let capturedInput: UpdateSiteInputInput
  let cachedOldSite: Record<string, unknown> | undefined

  return useMutation(
    (
      mutation,
      args: {
        siteId: string
        input: UpdateSiteInputInput
      }
    ) => {
      const { siteId, input } = args
      capturedSiteId = siteId
      capturedInput = input

      // Capture old site data from React Query cache before mutation
      const cachedSite = queryClient.getQueryData<Record<string, unknown>>([
        SITE_BY_ID_KEY,
        siteId,
      ]) as Record<string, unknown> | undefined
      if (cachedSite) {
        cachedOldSite = {
          name: cachedSite.name,
          description: cachedSite.description,
          countryId: cachedSite.countryId,
          stateId: cachedSite.stateId,
          addressStreet: cachedSite.addressStreet,
          addressStreet2: cachedSite.addressStreet2,
          addressCity: cachedSite.addressCity,
          addressState: cachedSite.addressState,
          addressCountry: cachedSite.addressCountry,
          addressPostalCode: cachedSite.addressPostalCode,
          phone: cachedSite.phone,
          displayBusinessHours: cachedSite.displayBusinessHours,
          businessHours: cachedSite.businessHours,
        }
      }

      const result = mutation.updateSite({
        siteId,
        input,
      })

      if (result) {
        void result.siteId
        void result.name
        void result.description
        void result.countryId
        void result.stateId
        void result.addressStreet
        void result.addressStreet2
        void result.addressCity
        void result.addressState
        void result.addressCountry
        void result.addressPostalCode
        void result.phone
        void result.displayBusinessHours
        void result.businessHours?.hasCustomHours
        void result.businessHours?.startTime
        void result.businessHours?.endTime
        void result.businessHours?.customHours?.monday?.startTime
        void result.businessHours?.customHours?.monday?.endTime
        void result.businessHours?.customHours?.tuesday?.startTime
        void result.businessHours?.customHours?.tuesday?.endTime
        void result.businessHours?.customHours?.wednesday?.startTime
        void result.businessHours?.customHours?.wednesday?.endTime
        void result.businessHours?.customHours?.thursday?.startTime
        void result.businessHours?.customHours?.thursday?.endTime
        void result.businessHours?.customHours?.friday?.startTime
        void result.businessHours?.customHours?.friday?.endTime
        void result.businessHours?.customHours?.saturday?.startTime
        void result.businessHours?.customHours?.saturday?.endTime
        void result.businessHours?.customHours?.sunday?.startTime
        void result.businessHours?.customHours?.sunday?.endTime
      }
      return result
    },
    {
      onComplete: (data) => {
        void queryClient.invalidateQueries([getSiteById.key])
        void queryClient.invalidateQueries([SITE_BY_ID_KEY])

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.site.toasts.updateSuccess',
            'Site updated successfully'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })

        // Track audit event (skip if caller handles its own tracking)
        if (!options?.skipAudit) {
          const { onSuccess: auditOnSuccess } = createMutationCallbacks({
            action: AuditAction.SETTING_UPDATED,
            resource: AuditResource.SETTING,
            resourceId: capturedSiteId,
            changeType: 'update',
            severity: AuditSeverity.INFO,
            featureCategory: 'settings',
            settingName: 'site.details',
            settingDisplayName: 'Site Details',
            getOldValue: () => cachedOldSite,
            getNewValue: () => capturedInput,
            getSummary: () => `Site settings updated`,
          })
          auditOnSuccess(data)
        }

        options?.onSuccess?.(data)
      },
      onError: (error: Error) => {
        // Track audit error (skip if caller handles its own tracking)
        if (!options?.skipAudit) {
          const { onError: auditOnError } = createMutationCallbacks({
            action: AuditAction.SETTING_UPDATED,
            resource: AuditResource.SETTING,
            changeType: 'update',
            featureCategory: 'settings',
            settingName: 'site.details',
            settingDisplayName: 'Site Details',
          })
          auditOnError(error)
        }

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.site.toasts.updateFailed',
            'Site update failed'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
        options?.onError?.(error)
      },
    }
  )
}

export const useToggleSiteBusinessHoursDisplay = (options?: MutationOptions) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const queryClient = useQueryClient()

  // Get audit config from context
  const auditConfig = useAuditTrackerConfigOptional()
  const { createMutationCallbacks } = useAuditTracker({
    ...auditConfig,
    corporateId: auditConfig?.corporateId ?? '',
  })

  let capturedArgs: { siteId: string; displayBusinessHours: boolean }
  let cachedOldDisplayBusinessHours: boolean | undefined

  return useMutation(
    (
      mutation,
      args: {
        siteId: string
        displayBusinessHours: boolean
      }
    ) => {
      const { siteId, displayBusinessHours } = args
      capturedArgs = args

      // Capture old value from React Query cache before mutation
      const cachedSite = queryClient.getQueryData<Record<string, unknown>>([
        SITE_BY_ID_KEY,
        siteId,
      ]) as Record<string, unknown> | undefined
      cachedOldDisplayBusinessHours = cachedSite?.displayBusinessHours as boolean | undefined

      const result = mutation.updateSiteSettingsBusinessLocations({
        siteId,
        input: { displayBusinessHours },
      })

      if (result) {
        void result.siteId
        void result.displayBusinessHours
      }
      return result
    },
    {
      onComplete: (data) => {
        void queryClient.invalidateQueries([getSiteById.key])
        void queryClient.invalidateQueries([SITE_BY_ID_KEY])

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.businessHours.toasts.displayUpdated',
            'Business hours display updated'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })

        // Track audit event
        const { onSuccess: auditOnSuccess } = createMutationCallbacks({
          action: AuditAction.SETTING_UPDATED,
          resource: AuditResource.SETTING,
          resourceId: capturedArgs?.siteId,
          changeType: 'update',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings',
          settingName: 'site.displayBusinessHours',
          settingDisplayName: 'Display Business Hours',
          getOldValue: () => cachedOldDisplayBusinessHours,
          getNewValue: () => capturedArgs?.displayBusinessHours,
          getSummary: () =>
            `Business hours display ${capturedArgs?.displayBusinessHours ? 'enabled' : 'disabled'}`,
        })
        auditOnSuccess(data)

        options?.onSuccess?.(data)
      },
      onError: (error: Error) => {
        // Track audit error
        const { onError: auditOnError } = createMutationCallbacks({
          action: AuditAction.SETTING_UPDATED,
          resource: AuditResource.SETTING,
          changeType: 'update',
          featureCategory: 'settings',
          settingName: 'site.displayBusinessHours',
          settingDisplayName: 'Display Business Hours',
        })
        auditOnError(error)

        openToast({
          title: t(
            'settings.systemSettings.locationSettings.businessHours.toasts.updateFailed',
            'Display toggle failed'
          ),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
        options?.onError?.(error)
      },
    }
  )
}
